import { type ReactNode, useCallback } from 'react';
import { TOUR_STEPS } from '../../App/OnBoarding/OnBoardingTour';
import { Fade, MenuItem } from '@mui/material';
import { useMenuContext, useSelectedLinkContext } from 'contexts';
import { trackEvent, GACommonEventAction, GAEventCategory } from 'services/googleAnalytics';
import {useFormatMessage, useNoWebUserLinkSnackbar} from 'hooks';
import {makeStyles} from '@mui/styles';
import {navigateToSelectedChannelRoute} from 'utils';
import type Route from 'routes/Routes';
import clsx from 'clsx';

const useStyles = makeStyles(() => ({
  item: {
    padding: '8.5px 18px 8.5px 10px',
    display: 'flex',
    gap: '8px'
  },
}), {name: 'AppMenuItem'});

type AppMenuItemProps = {
  enableWithoutSelectedLink?: boolean;
  icon: ReactNode;
  labelMessageId: string;
  step?: TOUR_STEPS;
  route: Route;
};

const AppMenuItem = ({
  enableWithoutSelectedLink,
  labelMessageId,
  step,
  icon,
  route
}: AppMenuItemProps): JSX.Element | null => {
  const { getMenuSelectedClassNames, shouldShowMenu, onMenuItemClick } = useMenuContext();
  const { selectedLink } = useSelectedLinkContext();
  const showNoWebUserLinkSnackbar = useNoWebUserLinkSnackbar(selectedLink);

  const label = useFormatMessage({ id: labelMessageId });


  const handleClick = useCallback(() => {
    if (!enableWithoutSelectedLink && !selectedLink) {
      showNoWebUserLinkSnackbar();
    } else {
      trackEvent(
        GAEventCategory.MENU,
        GACommonEventAction.CLICK,
        label,
      );
      
      navigateToSelectedChannelRoute(selectedLink, route);

      if (onMenuItemClick) onMenuItemClick();
    }
  }, [enableWithoutSelectedLink, selectedLink, showNoWebUserLinkSnackbar, label, route, onMenuItemClick]);

  const show = shouldShowMenu(step);
  const menuSelectedClassNames = getMenuSelectedClassNames(step);

  const classes = useStyles();
  return (
    <Fade in={show}>
      <MenuItem
        className={clsx(classes.item, menuSelectedClassNames)}
        onClick={handleClick}
      >
        {icon}

        {label}
      </MenuItem>
    </Fade>
  );
};

export default AppMenuItem;